import { useMemo } from 'react';

import type { GridColDef } from '@mui/x-data-grid-pro';

import type { ConfigurationFeaturesResponse } from '@/api/REST/configuration-features';

/**
 * Column visibility requirement configuration.
 * Specifies feature flags that must be enabled for a column to be visible.
 */
export interface ColumnVisibilityRequirement {
  /** Feature flag key from ConfigurationFeaturesResponse */
  featureFlag?: keyof ConfigurationFeaturesResponse;
}

interface UseFilteredColumnsParams {
  columns: GridColDef[];
  features?: ConfigurationFeaturesResponse;
  requirements: Record<string, ColumnVisibilityRequirement>;
}

/**
 * Filters columns based on feature flags and settings.
 *
 * - Columns without requirements are always visible
 * - Columns with requirements are only visible if all requirements are met
 * - Returns empty array if features haven't loaded (prevents flash of feature-gated columns)
 *
 * @example
 * ```tsx
 * const filteredColumns = useFilteredColumns({
 *   columns: allColumns,
 *   features,
 *   requirements: {
 *     'ColumnIdName1': { featureFlag: 'BillingPeopleRoles' },
 *     'ColumnIdName2': { featureFlag: 'CostControl' },
 *   },
 * });
 * ```
 */
export const useFilteredColumns = ({
  columns,
  features,
  requirements,
}: UseFilteredColumnsParams): GridColDef[] => {
  return useMemo(() => {
    // If features haven't loaded yet, return empty array
    // to avoid showing columns that depend on system-wide feature settings
    if (!features) {
      return [];
    }

    return columns.filter((column) => {
      const requirement = requirements[column.field];

      // If no requirement, column is always visible
      if (!requirement) {
        return true;
      }

      // Check feature flag requirement
      if (requirement.featureFlag) {
        const featureEnabled = features[requirement.featureFlag];
        if (!featureEnabled) {
          return false;
        }
      }

      // All requirements passed
      return true;
    });
  }, [columns, features, requirements]);
};
